import type { ChatItem } from "@foreman/app-core";
import type { OfficeAgent, OfficeTask } from "@foreman/protocol";

/** One step of the plan as the chat shows it: numbered, owned, and saying which steps it waits for. */
export interface PlanStep {
  id: string;
  n: number;
  title: string;
  status: string;
  owner?: OfficeAgent;
  /** Numbers of the steps this one waits for. */
  after: number[];
  /** Another step waits for the same steps, so the two can go at once. */
  parallel: boolean;
}

export function planOf(tasks: OfficeTask[], agents: OfficeAgent[]): PlanStep[] {
  const number = new Map(tasks.map((t, i) => [t.id, i + 1]));
  const key = (t: OfficeTask) => (t.dependsOn ?? []).slice().sort().join(",");
  return tasks.map((t, i) => {
    const after = (t.dependsOn ?? []).map((d) => number.get(d)).filter((n): n is number => n !== undefined);
    const same = key(t) !== "" && tasks.some((o) => o.id !== t.id && key(o) === key(t));
    return { id: t.id, n: i + 1, title: t.title, status: t.status, owner: t.assignee ? agents.find((a) => a.id === t.assignee) : undefined, after, parallel: same };
  });
}

export type ChatEntry = ChatItem | { kind: "plan"; id: "plan" };

/** The plan sits where the coordinator announces it: right after its first answer to the latest thing you said (else right after that), else at the end. */
export function withPlan(items: ChatItem[], show: boolean): ChatEntry[] {
  if (!show) return items;
  let lastYou = -1;
  items.forEach((m, i) => {
    if (m.kind === "you") lastYou = i;
  });
  let at = items.length;
  if (lastYou >= 0) {
    const reply = items.findIndex((m, i) => i > lastYou && m.kind === "dot");
    at = reply >= 0 ? reply + 1 : lastYou + 1;
  }
  return [...items.slice(0, at), { kind: "plan", id: "plan" }, ...items.slice(at)];
}

export interface Pending {
  id: string;
  text: string;
  ts: number;
}

const norm = (s: string) => s.replace(/\s+/g, " ").trim();

/** What you just sent, shown at once: a bubble for each message the engine has not echoed back yet. */
export function withPending(items: ChatItem[], pending: Pending[]): ChatItem[] {
  const echoed = (p: Pending) => items.some((m) => m.kind === "you" && norm(m.text) === norm(p.text) && m.ts >= p.ts - 5000);
  const waiting = pending.filter((p) => !echoed(p)).map((p): ChatItem => ({ kind: "you", id: p.id, ts: p.ts, text: p.text }));
  return waiting.length === 0 ? items : [...items, ...waiting].sort((a, b) => a.ts - b.ts);
}

/** The ones still waiting, so the list does not grow without end. */
export function stillPending(items: ChatItem[], pending: Pending[], now: number): Pending[] {
  return pending.filter((p) => now - p.ts < 60_000 && !items.some((m) => m.kind === "you" && norm(m.text) === norm(p.text) && m.ts >= p.ts - 5000));
}

/** Is the coordinator working on something you said? True from your message until it answers (or for a few minutes), and while it is visibly busy. */
export function isTyping(items: ChatItem[], now: number, leadWorking: boolean): boolean {
  let lastYou = -1;
  items.forEach((m, i) => {
    if (m.kind === "you") lastYou = i;
  });
  if (lastYou < 0) return false;
  const answered = items.some((m, i) => i > lastYou && m.kind === "dot");
  if (answered) return false;
  return leadWorking || now - items[lastYou]!.ts < 180_000;
}

/** A short title for the plan card: what you asked for, first line, cut to fit. */
export function goalTitle(items: ChatItem[], max = 60): string {
  const you = [...items].reverse().find((m) => m.kind === "you");
  const line = you && you.kind === "you" ? you.text.split("\n")[0]!.trim() : "";
  return line ? (line.length > max ? line.slice(0, max - 1) + "…" : line) : "The plan";
}
